import { useState } from "react";
import { useTranslation } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";

/**
 * "Scroll to zoom · Drag to orbit · Right-drag to pan" — the recipient's only navigation affordance
 * in the 3D/2D viewer.
 *
 * It exists because a recipient's viewer has NO toolbar: FR-19 reduces the operator stack to
 * navigation only, so there are no visible orbit/pan/zoom buttons to discover the controls from. An
 * internal user learns them from the toolbar; a guest would otherwise be handed a black canvas.
 *
 * Dismissal is per session (see {@link ExpiryBanner} for the same reasoning): the hint has to survive
 * navigating between files in a shared folder without nagging on every one, and a recipient returning
 * in a new tab tomorrow is a new audience.
 */
const DISMISS_KEY = "cube.share.viewerNavHint.dismissed";

function readDismissed(): boolean {
  try {
    return globalThis.sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    // Safari private mode throws on sessionStorage access rather than returning null. Showing the
    // hint is the safe fallback; it is dismissible either way.
    return false;
  }
}

function ShareViewerNavigationHint() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => readDismissed());

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      globalThis.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Non-persistent dismissal is an acceptable degradation; refusing to dismiss is not.
    }
  };

  return (
    <div
      // `pointer-events-none` on the wrapper with the button re-enabling them: the hint floats over a
      // canvas that owns mouse capture for orbit and pan, and a plain overlay would swallow drags
      // starting anywhere underneath it.
      className="pointer-events-none absolute bottom-6 start-1/2 z-10 -translate-x-1/2"
    >
      <div className="pointer-events-auto flex items-center gap-4 rounded-md border border-stroke_primary bg-white px-4 py-2 shadow-lg">
        <Text className="text-xs text-text_light">{t("Scroll to zoom · Drag to orbit · Right-drag to pan")}</Text>
        <button type="button" onClick={handleDismiss} aria-label={t("Dismiss")}>
          <IconComponent name="close" width="0.75rem" height="0.75rem" />
        </button>
      </div>
    </div>
  );
}

export default ShareViewerNavigationHint;
